import { useState, useEffect, useRef } from "react";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import {
  ChevronLeft, ChevronRight, ChevronDown, MapPin, Clock, X, Plus, LayoutTemplate,
  Calendar as CalendarIcon, Loader2, FileDown, FileSpreadsheet, Lock, Info, HelpCircle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { apiClient, useAuth } from "@/App";
import { toast } from "sonner";
import {
  format, parseISO, isSameDay, startOfMonth, endOfMonth, isWithinInterval,
  addMonths, subMonths, startOfWeek, endOfWeek, eachDayOfInterval,
  addWeeks, subWeeks, addDays, subDays
} from "date-fns";
import { useNavigate } from "react-router-dom";
import { Kicker } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

// ─── Time-grid constants ─────────────────────────────────────────────────────
const GRID_START_HOUR = 7;   // 7 am
const GRID_END_HOUR   = 24;  // midnight
const HOUR_PX         = 48;  // pixels per hour
const TOTAL_HOURS     = GRID_END_HOUR - GRID_START_HOUR; // 17
const GRID_HEIGHT_PX  = TOTAL_HOURS * HOUR_PX;           // 816
const MIN_EVENT_PX    = 22;  // minimum block height so text is always readable

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + (isNaN(m) ? 0 : m);
};

/**
 * Returns { top, height } in pixels for a travel block before a gig.
 * Anchors to call_time when available, otherwise start_time.
 */
const travelGeometry = (startTime, callTime, travelMinutes) => {
  if (!travelMinutes || travelMinutes <= 0) return null;
  const gridStartMin = GRID_START_HOUR * 60;
  // Prefer call_time as the "must arrive by" anchor
  const anchorMin    = parseTimeToMinutes(callTime) ?? parseTimeToMinutes(startTime) ?? (9 * 60);
  const travelStart  = anchorMin - travelMinutes;
  const clampedStart = Math.max(travelStart, gridStartMin);
  const clampedEnd   = Math.min(anchorMin, GRID_END_HOUR * 60);
  const height = Math.max(((clampedEnd - clampedStart) / 60) * HOUR_PX, 10);
  const top    = ((clampedStart - gridStartMin) / 60) * HOUR_PX;
  return { top, height };
};

/** Returns { top, height } in pixels for an event on the time grid. */
const eventGeometry = (startTime, endTime) => {
  const gridStartMin = GRID_START_HOUR * 60;
  const gridEndMin   = GRID_END_HOUR   * 60;

  const startMin = parseTimeToMinutes(startTime) ?? (9 * 60); // default 9 am
  const endMin   = parseTimeToMinutes(endTime)   ?? (startMin + 60); // default +1 hr

  const clampedStart = Math.max(startMin, gridStartMin);
  const clampedEnd   = Math.min(Math.max(endMin, startMin + 15), gridEndMin);

  const top    = ((clampedStart - gridStartMin) / 60) * HOUR_PX;
  const height = Math.max(((clampedEnd - clampedStart) / 60) * HOUR_PX, MIN_EVENT_PX);

  return { top, height };
};

// ─── Hour labels column ──────────────────────────────────────────────────────
const HOURS = Array.from({ length: TOTAL_HOURS }, (_, i) => GRID_START_HOUR + i);

const HourLabels = () => (
  <div className="relative shrink-0" style={{ width: '2.75rem', height: GRID_HEIGHT_PX }}>
    {HOURS.map(hour => (
      <div
        key={hour}
        className="absolute right-1 text-[10px] text-muted-foreground leading-none"
        style={{ top: (hour - GRID_START_HOUR) * HOUR_PX - 6 }}
      >
        {hour < 12 ? `${hour}a` : hour === 12 ? '12p' : `${hour - 12}p`}
      </div>
    ))}
  </div>
);

// ─── Horizontal grid lines ───────────────────────────────────────────────────
const GridLines = () => (
  <>
    {HOURS.map(hour => (
      <div
        key={hour}
        className="absolute left-0 right-0 border-t border-border/20 pointer-events-none"
        style={{ top: (hour - GRID_START_HOUR) * HOUR_PX }}
      />
    ))}
  </>
);

export const CalendarView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [backgroundEvents, setBackgroundEvents] = useState([]);
  const [viewMode, setViewMode] = useState('month');
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [currentDay, setCurrentDay] = useState(new Date());
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [exporting, setExporting] = useState(null);
  const sheetSwipeRef = useRef({ x: 0, y: 0, active: false, mode: null, dy: 0 });
  const sheetContentRef = useRef(null); // scrollable content — used to gate dismiss on scrollTop
  const [sheetDragY, setSheetDragY] = useState(0);      // live translateY while dragging the sheet
  const [sheetDragging, setSheetDragging] = useState(false); // disables transition for 1:1 finger tracking

  useEffect(() => {
    fetchGigs();
    fetchBackgroundEvents();
  }, []);

  // Keyboard day navigation in Month view (desktop) — left/right move a day,
  // up/down move a calendar row (7 days), matching the grid layout. Skipped
  // while focus is in a text input so it doesn't hijack typing elsewhere on
  // the page. Crossing a month boundary follows the grid onto that month.
  useEffect(() => {
    if (viewMode !== 'month') return;
    const onKeyDown = (e) => {
      const delta = e.key === 'ArrowLeft' ? -1
        : e.key === 'ArrowRight' ? 1
        : e.key === 'ArrowUp' ? -7
        : e.key === 'ArrowDown' ? 7
        : null;
      if (delta === null) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      const next = addDays(selectedDate, delta);
      setSelectedDate(next);
      if (next.getMonth() !== currentMonth.getMonth() || next.getFullYear() !== currentMonth.getFullYear()) {
        setCurrentMonth(next);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [viewMode, selectedDate, currentMonth]);

  const fetchGigs = async () => {
    try {
      const response = await apiClient.get("/gigs?time_filter=all");
      setGigs(response.data);
    } catch (error) {
      console.error("Failed to fetch gigs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackgroundEvents = async () => {
    try {
      const response = await apiClient.get("/calendar/all-events");
      setBackgroundEvents(response.data.events || []);
    } catch {
      // Not connected — silently ignore
    }
  };

  const downloadBlob = (data, filename, mimeType) => {
    const blob = new Blob([data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    requestAnimationFrame(() => {
      link.click();
      setTimeout(() => {
        try { document.body.removeChild(link); } catch {}
        window.URL.revokeObjectURL(url);
      }, 500);
    });
  };

  const getFilenameFromHeaders = (headers, fallback) => {
    const cd = headers?.['content-disposition'] || '';
    const match = cd.match(/filename=([^\s;]+)/);
    return match ? match[1] : fallback;
  };

  const exportIcal = async () => {
    try {
      const response = await apiClient.get("/gigs/export/ical", { responseType: 'blob' });
      downloadBlob(response.data, 'fretless_gigs.ics', 'text/calendar');
      toast.success("Calendar exported as iCal file");
    } catch {
      toast.error("Failed to export calendar");
    }
  };

  const exportCsv = async (fmt) => {
    setExporting(fmt);
    try {
      const response = await apiClient.get(`/gigs/export/csv?format=${fmt}`, { responseType: 'blob' });
      const contentType = response.headers?.['content-type'] || 'text/csv';
      const isZip = contentType.includes('application/zip');
      const fallbackName = isZip ? `fretless-${fmt}.zip` : `fretless-${fmt}.csv`;
      const filename = getFilenameFromHeaders(response.headers, fallbackName);
      const mimeType = isZip ? 'application/zip' : 'text/csv';
      downloadBlob(response.data, filename, mimeType);
      const labels = { songkick: 'Songkick', bandsintown: 'Bandsintown', generic: 'Generic' };
      toast.success(`${labels[fmt] || 'CSV'} export downloaded${isZip ? ' (zip archive)' : ''}`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("CSV export is a Pro feature. Upgrade to Pro to unlock it.");
      } else {
        toast.error("Failed to export CSV");
      }
    } finally {
      setExporting(null);
    }
  };

  // ─── Derived data ────────────────────────────────────────────────────────────
  /** Returns true when `day` falls on or between a gig's start and end dates. */
  const gigCoversDay = (gig, day) => {
    try {
      const dayStr = format(day, 'yyyy-MM-dd');
      const startStr = gig.date.slice(0, 10);
      const endStr = gig.end_date ? gig.end_date.slice(0, 10) : startStr;
      return dayStr >= startStr && dayStr <= endStr;
    } catch { return false; }
  };

  const selectedDateGigs = gigs.filter(gig => gigCoversDay(gig, selectedDate));

  const selectedDateBgEvents = backgroundEvents.filter(evt => {
    try { return evt.date && isSameDay(parseISO(evt.date), selectedDate); } catch { return false; }
  });

  const gigsInMonth = gigs.filter(gig => {
    try {
      const monthStart = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      const gigStart = gig.date.slice(0, 10);
      const gigEnd = gig.end_date ? gig.end_date.slice(0, 10) : gigStart;
      return gigStart <= monthEnd && gigEnd >= monthStart;
    } catch { return false; }
  });

  // Build the set of all days in the month that have at least one gig
  const datesWithGigs = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  }).filter(day => gigsInMonth.some(gig => gigCoversDay(gig, day)));

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-500';
      case 'pending':   return 'bg-orange-400';
      case 'cancelled': return 'bg-red-500';
      default:          return 'bg-emerald-500';
    }
  };

  // "Add Gig" entry point, reused across Day/Week/Month views. Matches the
  // Gigs page's own "Add Gig" dropdown (blank gig vs. from a saved template)
  // instead of being a plain shortcut to a blank gig — so this is a real
  // second entry point, not a cut-down one. The template option round-trips
  // through the Gigs list page (that's where the template picker dialog and
  // saved-templates list already live) with ?openTemplatePicker=true, and
  // ?date= rides along both paths so the date already chosen here doesn't
  // have to be re-picked.
  const AddGigMenu = ({ date, testId, variant = "full", onBeforeNavigate }) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const go = (path) => {
      onBeforeNavigate?.();
      navigate(path);
    };
    const iconOnly = variant === "icon";
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "flex items-center gap-1 font-medium text-primary-foreground transition-opacity hover:opacity-90",
              iconOnly ? "w-5 h-5 justify-center rounded-full" : "rounded-md text-xs",
              !iconOnly && (variant === "compact" ? "px-2 py-1" : "px-2.5 py-1.5")
            )}
            style={{ backgroundColor: tokens.color.orange }}
            aria-label="Add gig on this day"
            data-testid={testId}
          >
            <Plus className={iconOnly ? "h-3 w-3" : "h-3.5 w-3.5"} />
            {variant === "full" && "Add Gig"}
            {variant === "compact" && "Add"}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => go(`/gigs/new?date=${dateStr}`)}>
            <Plus className="h-4 w-4 mr-2" />
            Add new gig
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => go(`/gigs?openTemplatePicker=true&date=${dateStr}`)}>
            <LayoutTemplate className="h-4 w-4 mr-2" />
            Create from template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // ─── Month grid ──────────────────────────────────────────────────────────────
  const MonthView = () => {
    const year  = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    // (day + 6) % 7 maps Sun=0→6, Mon=1→0, Tue=2→1 … so Monday is column 0.
    const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth    = new Date(year, month + 1, 0).getDate();
    const today          = new Date();

    const cells = [];
    for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    const tail = cells.length % 7;
    if (tail !== 0) for (let i = 0; i < 7 - tail; i++) cells.push(null);

    const hasGig = (date) => datesWithGigs.some(d => isSameDay(d, date));

    return (
      <div className="w-full select-none overflow-x-auto">
        {/* Weekday headers with proper spacing */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: `${tokens.space.xxs}px`,
            marginBottom: `${tokens.space.sm}px`,
            minWidth: 'min-content',
          }}
        >
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(label => (
            <div
              key={label}
              className="text-center"
              style={{
                ...tokens.type.monoCaptionSm,
                color: tokens.color.textMuted,
                padding: `${tokens.space.xxs}px`,
                minWidth: `${tokens.space.xxxl}px`,
              }}
            >
              {label}
            </div>
          ))}
        </div>

        {/* Calendar grid with explicit grid layout */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: `${tokens.space.xxs}px`,
            minWidth: 'min-content',
          }}
        >
          {cells.map((date, idx) => {
            if (!date) return <div key={`pad-${idx}`} aria-hidden="true" style={{ minWidth: `${tokens.space.xxxl}px` }} />;
            const isSelected = isSameDay(date, selectedDate);
            const isToday    = isSameDay(date, today);
            const gigDot     = hasGig(date);
            return (
              <div
                key={date.toISOString()}
                role="button"
                tabIndex={0}
                aria-label={format(date, 'MMMM d, yyyy')}
                aria-pressed={isSelected}
                onClick={() => { setSelectedDate(date); setMobileSheetOpen(true); }}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setSelectedDate(date)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  padding: `${tokens.space.sm}px ${tokens.space.xxs}px`,
                  borderRadius: `${tokens.radius.sm}px`,
                  cursor: 'pointer',
                  transition: `all ${tokens.motion.fast}`,
                  backgroundColor: isSelected ? tokens.color.innerBg :
                                    isToday ? tokens.color.orangeSoft : 'transparent',
                  border: `1px solid ${isSelected || isToday ? tokens.color.orange : 'transparent'}`,
                  minWidth: `${tokens.space.xxxl}px`,
                  minHeight: `${tokens.space.xxxl}px`,
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = tokens.color.innerBg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected ? tokens.color.innerBg :
                                                          isToday ? tokens.color.orangeSoft : 'transparent';
                }}
              >
                <span style={{
                  ...tokens.type.bodySm,
                  lineHeight: '1',
                  fontWeight: isToday || isSelected ? 700 : 400,
                  color: isToday ? tokens.color.orange : tokens.color.text,
                  marginBottom: `${tokens.space.xxs}px`,
                }}>
                  {date.getDate()}
                </span>
                {gigDot && (
                  <div style={{
                    width: `${tokens.space.xxs}px`,
                    height: `${tokens.space.xxs}px`,
                    borderRadius: tokens.radius.pill,
                    backgroundColor: tokens.color.orange,
                    marginTop: `${tokens.space.xxs}px`,
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Week view (desktop) ─────────────────────────────────────────────────────
  const WeekView = () => {
    const weekDays = eachDayOfInterval({
      start: currentWeek,
      end: endOfWeek(currentWeek, { weekStartsOn: 1 })
    });

    const gigsForDay = (day) => gigs.filter(g => gigCoversDay(g, day));

    const bgEventsForDay = (day) => backgroundEvents.filter(evt => {
      try { return evt.date && isSameDay(parseISO(evt.date), day); } catch { return false; }
    });

    return (
      <div className="overflow-y-auto max-h-[580px]">
        {/* Sticky day headers */}
        <div
          className="sticky top-0 z-10 bg-card grid border-b border-border"
          style={{ gridTemplateColumns: '2.75rem repeat(7, 1fr)' }}
        >
          <div className="p-1" />
          {weekDays.map(day => (
            <div
              key={day.toISOString()}
              className={cn(
                "p-2 text-center border-l border-border/30",
                isSameDay(day, selectedDate) && "bg-secondary/30"
              )}
            >
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                {format(day, 'EEE')}
              </div>
              <div className="flex items-center justify-center gap-0.5 mt-1">
                <div
                  className={cn(
                    "w-7 h-7 flex items-center justify-center rounded-full text-sm",
                    "cursor-pointer hover:bg-secondary/50 transition-colors",
                    isSameDay(day, new Date()) && "bg-primary text-primary-foreground hover:bg-primary"
                  )}
                  onClick={() => setSelectedDate(day)}
                >
                  {format(day, 'd')}
                </div>
                <AddGigMenu date={day} variant="icon" testId={`calendar-week-add-gig-${format(day, 'yyyy-MM-dd')}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Absolute-positioned time grid */}
        <div className="flex">
          <HourLabels />
          <div className="flex-1 grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {weekDays.map(day => {
              const dayGigs     = gigsForDay(day);
              const dayBgEvents = bgEventsForDay(day);
              return (
                <div
                  key={day.toISOString()}
                  className={cn(
                    "relative border-l border-border/20",
                    isSameDay(day, selectedDate) && "bg-secondary/10"
                  )}
                  style={{ height: GRID_HEIGHT_PX }}
                >
                  <GridLines />

                  {dayGigs.map(gig => {
                    const { top, height } = eventGeometry(gig.start_time, gig.end_time);
                    const travel = travelGeometry(gig.start_time, gig.call_time, gig.estimated_travel_minutes);
                    return (
                      <div key={gig.gig_id}>
                        {travel && (
                          <div
                            className="absolute overflow-hidden rounded pointer-events-none"
                            style={{ top: travel.top, height: travel.height, left: 2, right: 2, backgroundColor: 'rgba(113,113,122,0.35)', borderTop: '1px solid rgba(113,113,122,0.6)' }}
                          >
                            <div className="px-1 py-0.5 text-[9px] leading-tight text-zinc-300 truncate">🚗</div>
                          </div>
                        )}
                        <div
                          className="absolute overflow-hidden rounded cursor-pointer hover:opacity-80 transition-opacity"
                          style={{ backgroundColor: tokens.color.orange, top, height, left: 2, right: 2 }}
                          onClick={() => navigate('/gigs', { state: { highlightGigId: gig.gig_id } })}
                          title={`${gig.artist_name} @ ${gig.venue_name}${gig.start_time ? ' · ' + gig.start_time : ''}${gig.estimated_travel_minutes ? ` · ~${gig.estimated_travel_minutes}min travel` : ''}`}
                        >
                          <div className="px-1 py-0.5 text-[10px] leading-tight text-white font-medium truncate">
                            {gig.artist_name}
                          </div>
                          <div className="px-1 pb-0.5 text-[9px] leading-tight text-white/80 truncate">
                            {gig.venue_name}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {dayBgEvents.map(evt => {
                    const { top, height } = eventGeometry(evt.start_time, evt.end_time);
                    return (
                      <div
                        key={evt.id}
                        className="absolute overflow-hidden rounded cursor-default bg-zinc-600/50"
                        style={{ top, height, left: 2, right: 2 }}
                        title={`${evt.title}${evt.start_time ? ' · ' + evt.start_time : ''}${evt.calendar_name ? ' — ' + evt.calendar_name : ''}`}
                      >
                        <div className="px-1 py-0.5 text-[10px] leading-tight text-zinc-300 truncate">
                          {evt.title}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ─── Day view (mobile + desktop) ─────────────────────────────────────────────
  const DayView = () => {
    const dayGigs = gigs.filter(g => gigCoversDay(g, currentDay));
    const dayBgEvents = backgroundEvents.filter(evt => {
      try { return evt.date && isSameDay(parseISO(evt.date), currentDay); } catch { return false; }
    });

    return (
      <div
        className="overflow-y-auto max-h-[580px]"
        style={{
          transform: swipeHint === 'left' ? 'translateX(-8px)'
            : swipeHint === 'right' ? 'translateX(8px)' : 'translateX(0)',
          transition: 'transform 0.15s ease-out',
        }}
      >
        {/* Day header — swipe handlers live here (non-scrollable) so iOS
            doesn't cancel the touch sequence for native scroll */}
        <div
          className="sticky top-0 z-10 bg-card border-b border-border select-none"
          onTouchStart={onDaySwipeStart}
          onTouchMove={onDaySwipeMove}
          onTouchEnd={onDaySwipeEnd}
          onTouchCancel={onDaySwipeEnd}
          style={{ touchAction: 'pan-y' }}
        >
          <div className="flex items-center justify-between px-2 py-2">
            <button
              onClick={handlePrev}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Previous day"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-9 h-9 flex items-center justify-center rounded-full text-base font-semibold shrink-0",
                isSameDay(currentDay, new Date())
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary/50 text-foreground"
              )}>
                {format(currentDay, 'd')}
              </div>
              <div>
                <div className="text-sm font-medium leading-tight">{format(currentDay, 'EEEE')}</div>
                <div className="text-xs text-muted-foreground">{format(currentDay, 'MMMM yyyy')}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleNext}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Next day"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <AddGigMenu date={currentDay} variant="full" testId="calendar-day-add-gig" />
            </div>
          </div>
        </div>

        {/* Absolute-positioned time grid */}
        <div className="flex">
          <HourLabels />
          <div className="relative flex-1" style={{ height: GRID_HEIGHT_PX }}>
            <GridLines />

            {dayGigs.length === 0 && dayBgEvents.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-sm text-muted-foreground">No events</p>
              </div>
            )}

            {dayGigs.map(gig => {
              const { top, height } = eventGeometry(gig.start_time, gig.end_time);
              const travel = travelGeometry(gig.start_time, gig.call_time, gig.estimated_travel_minutes);
              return (
                <div key={gig.gig_id}>
                  {travel && (
                    <div
                      className="absolute overflow-hidden rounded pointer-events-none"
                      style={{ top: travel.top, height: travel.height, left: 4, right: 4, backgroundColor: 'rgba(113,113,122,0.35)', borderTop: '1px solid rgba(113,113,122,0.6)' }}
                    >
                      <div className="px-2 py-0.5 text-[10px] leading-tight text-zinc-300 truncate">
                        🚗 {gig.estimated_travel_minutes}min travel
                      </div>
                    </div>
                  )}
                  <div
                    className="absolute overflow-hidden rounded cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: tokens.color.orange, top, height, left: 4, right: 4 }}
                    onClick={() => navigate('/gigs', { state: { highlightGigId: gig.gig_id } })}
                    title={`${gig.artist_name} @ ${gig.venue_name}${gig.start_time ? ' · ' + gig.start_time : ''}${gig.estimated_travel_minutes ? ` · ~${gig.estimated_travel_minutes}min travel` : ''}`}
                  >
                    <div className="px-2 py-1 text-xs leading-tight text-white font-semibold truncate">
                      {gig.artist_name}
                    </div>
                    <div className="px-2 pb-1 text-[10px] leading-tight text-white/80 truncate">
                      {gig.venue_name}
                      {gig.start_time && ` · ${gig.start_time}${gig.end_time ? ` – ${gig.end_time}` : ''}`}
                    </div>
                  </div>
                </div>
              );
            })}

            {dayBgEvents.map(evt => {
              const { top, height } = eventGeometry(evt.start_time, evt.end_time);
              return (
                <div
                  key={evt.id}
                  className="absolute overflow-hidden rounded cursor-default bg-zinc-600/50"
                  style={{ top, height, left: 4, right: 4 }}
                  title={`${evt.title}${evt.start_time ? ' · ' + evt.start_time : ''}`}
                >
                  <div className="px-2 py-1 text-xs leading-tight text-zinc-300 truncate">
                    {evt.title}
                  </div>
                  {evt.start_time && (
                    <div className="px-2 pb-1 text-[10px] text-zinc-400 truncate">
                      {evt.start_time}{evt.end_time ? ` – ${evt.end_time}` : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ─── Navigation ──────────────────────────────────────────────────────────────
  const handlePrev = () => {
    if (viewMode === 'month') setCurrentMonth(subMonths(currentMonth, 1));
    else if (viewMode === 'week') setCurrentWeek(subWeeks(currentWeek, 1));
    else setCurrentDay(subDays(currentDay, 1));
  };

  const handleNext = () => {
    if (viewMode === 'month') setCurrentMonth(addMonths(currentMonth, 1));
    else if (viewMode === 'week') setCurrentWeek(addWeeks(currentWeek, 1));
    else setCurrentDay(addDays(currentDay, 1));
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setCurrentWeek(startOfWeek(today, { weekStartsOn: 1 }));
    setCurrentDay(today);
    setSelectedDate(today);
  };

  // ─── Swipe navigation (day view) ─────────────────────────────────────────────
  // Track touch start; on release, a mostly-horizontal swipe past the
  // threshold moves to the prev/next day. We require the gesture to be more
  // horizontal than vertical so it doesn't hijack the day view's vertical
  // time-grid scroll.
  const swipeRef = useRef({ x: 0, y: 0, active: false, triggered: false });
  const SWIPE_THRESHOLD = 60; // px
  const [swipeHint, setSwipeHint] = useState(null); // 'left' | 'right' | null

  const onDaySwipeStart = (e) => {
    const t = e.touches[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY, active: true, triggered: false };
  };

  // Detect the swipe during touchmove and fire as soon as the threshold is
  // crossed. iOS Safari often sends `touchcancel` instead of `touchend` once
  // it decides a gesture inside a scrollable container might be a scroll —
  // relying on touchend alone meant the swipe never registered there.
  const onDaySwipeMove = (e) => {
    const state = swipeRef.current;
    if (!state.active || state.triggered) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - state.x;
    const dy = t.clientY - state.y;
    // Horizontal-dominant swipe only.
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5) {
      state.triggered = true;
      if (dx < 0) handleNext();
      else handlePrev();
      // Brief directional hint for feedback.
      setSwipeHint(dx < 0 ? 'left' : 'right');
      setTimeout(() => setSwipeHint(null), 250);
    }
  };

  const onDaySwipeEnd = () => {
    swipeRef.current.active = false;
    swipeRef.current.triggered = false;
  };

  // ─── Sheet swipe / drag (mobile month-view day card) ──────────────────────
  // Gesture direction is locked once the finger leaves a small deadzone:
  //   - horizontal → navigate to the adjacent day
  //   - vertical down (with content scrolled to the top) → finger-tracked
  //     drag; release past the threshold dismisses, otherwise it snaps back
  //   - vertical otherwise → let the content scroll naturally
  const SHEET_H_THRESHOLD = 60;   // px horizontal to change day
  const SHEET_DISMISS = 110;      // px dragged-down on release to dismiss
  const SHEET_DEADZONE = 10;      // px before we commit to a direction
  const SHEET_DRAG_ACTIVATE = 16; // px of downward travel before the sheet visibly follows

  // Animate the sheet fully out, then unmount. Shared by drag-dismiss,
  // backdrop tap, and any future close affordance so they feel identical.
  const closeSheet = () => {
    setSheetDragging(false);
    setSheetDragY(window.innerHeight);
    setTimeout(() => {
      setMobileSheetOpen(false);
      setSheetDragY(0);
    }, 240);
  };

  const onSheetSwipeStart = (e) => {
    const t = e.touches[0];
    if (!t) return;
    sheetSwipeRef.current = { x: t.clientX, y: t.clientY, active: true, mode: null, dy: 0 };
  };

  const onSheetSwipeMove = (e) => {
    const s = sheetSwipeRef.current;
    if (!s.active) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    // Lock direction once out of the deadzone. Bias toward horizontal so a
    // left/right swipe with a little vertical jitter doesn't briefly grab the
    // sheet and cause a dip-then-bounce. Only a *clearly* downward gesture
    // (and only with the content scrolled to the top) starts a drag-dismiss.
    if (!s.mode) {
      if (adx < SHEET_DEADZONE && ady < SHEET_DEADZONE) return;
      const atTop = !sheetContentRef.current || sheetContentRef.current.scrollTop < 5;
      if (dy > 0 && ady > adx * 1.3 && atTop) s.mode = "vertical";
      else if (adx >= ady) s.mode = "horizontal";
      else s.mode = "scroll";
    }

    if (s.mode === "vertical") {
      s.dy = Math.max(0, dy);
      const visible = s.dy - SHEET_DRAG_ACTIVATE;
      if (visible <= 0) {
        // Sheet hasn't visibly moved yet — let an emerging horizontal swipe
        // take over so an arcing gesture still navigates without any dip.
        if (adx > ady && adx > SHEET_DEADZONE) s.mode = "horizontal";
      } else {
        if (!sheetDragging) setSheetDragging(true);
        setSheetDragY(visible);
      }
    }

    if (s.mode === "horizontal" && adx > SHEET_H_THRESHOLD) {
      setSelectedDate(dx < 0 ? addDays(selectedDate, 1) : subDays(selectedDate, 1));
      s.active = false; // one day-change per gesture
    }
  };

  const onSheetSwipeEnd = () => {
    const s = sheetSwipeRef.current;
    s.active = false;
    if (s.mode === "vertical") {
      setSheetDragging(false); // re-enable transition for snap / dismiss
      if (s.dy > SHEET_DISMISS) closeSheet();
      else setSheetDragY(0);   // snap back
    }
    s.mode = null;
  };

  const weekStart = currentWeek;
  const weekEnd   = endOfWeek(currentWeek, { weekStartsOn: 1 });

  const titleLabel = () => {
    if (viewMode === 'month') return format(currentMonth, 'MMMM yyyy');
    if (viewMode === 'week')  return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`;
    return format(currentDay, 'EEEE, MMMM d');
  };

  return (
    <Layout>
      <div data-testid="calendar-page">
        {/* Page header — Export buttons + 'how to upload' now live below
            the calendar (per the user's layout brief), so the header is
            just kicker + h1. */}
        <div style={{ marginBottom: tokens.space.xxl }}>
          <Kicker color={tokens.color.orange} style={{ marginBottom: tokens.space.xs }}>
            Calendar
          </Kicker>
          <h1
            style={{
              margin: 0,
              ...tokens.type.heroSm,
              color: tokens.color.text,
              textWrap: "balance",
            }}
          >
            Gig Calendar
          </h1>
        </div>

        {/* Export buttons + 'how to upload' help live below the calendar
            card — see the section after the calendar grid. */}

        {/* Calendar card — 2/3 width for month (sidebar fits), full width for day/week */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className={cn(
            "bg-card border-border",
            viewMode === 'month' ? "lg:col-span-2" : "lg:col-span-3"
          )}>
            <CardHeader className="pb-4 border-b border-border/50">
              {/* Improved header: Month name prominent on left, navigation on right */}
              <div className="flex items-center justify-between">
                {/* Left: Month & Year display */}
                <div>
                  <div
                    style={{
                      fontFamily: tokens.font.display,
                      fontWeight: 700,
                      fontSize: viewMode === 'month' ? 28 : 18,
                      letterSpacing: "-0.01em",
                      color: tokens.color.text,
                      lineHeight: 1,
                      marginBottom: viewMode === 'month' ? "4px" : 0,
                    }}
                  >
                    {viewMode === 'month' ? format(currentMonth, 'MMMM') : titleLabel()}
                  </div>
                  {viewMode === 'month' && (
                    <div style={{
                      fontSize: 13,
                      color: tokens.color.textMuted,
                      fontWeight: 400,
                    }}>
                      {format(currentMonth, 'yyyy')}
                    </div>
                  )}
                </div>

                {/* Right: Navigation controls */}
                <div className="flex items-center gap-2 shrink-0">
                  {/* Desktop: Month/Week/Day toggle — always visible */}
                  <div
                      className="hidden md:flex overflow-hidden mr-1"
                      style={{
                        borderRadius: tokens.radius.md,
                        border: tokens.border.card,
                      }}
                    >
                      {['month', 'week'].map((mode, i) => {
                        const active = viewMode === mode;
                        return (
                          <button
                            key={mode}
                            onClick={() => setViewMode(mode)}
                            data-testid={`view-${mode}`}
                            style={{
                              padding: "6px 12px",
                              ...tokens.type.bodySm,
                              fontWeight: 500,
                              background: active ? tokens.color.innerBg : "transparent",
                              color: active ? tokens.color.text : tokens.color.textMuted,
                              border: "none",
                              borderLeft: i > 0 ? tokens.border.card : "none",
                              cursor: "pointer",
                              transition: `color ${tokens.motion.fast}, background ${tokens.motion.fast}`,
                            }}
                            onMouseEnter={(e) => {
                              if (!active) e.currentTarget.style.color = tokens.color.text;
                            }}
                            onMouseLeave={(e) => {
                              if (!active) e.currentTarget.style.color = tokens.color.textMuted;
                            }}
                          >
                            {mode.charAt(0).toUpperCase() + mode.slice(1)}
                          </button>
                        );
                      })}
                    </div>

                  {/* Navigation buttons */}
                  <Button variant="outline" size="icon" onClick={handlePrev} data-testid="prev-month">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleToday} data-testid="today-btn">
                    Today
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleNext} data-testid="next-month">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {viewMode === 'month' && (
                <>
                  <MonthView />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space.lg,
                      marginTop: tokens.space.lg,
                      paddingTop: tokens.space.lg,
                      borderTop: tokens.border.hairline,
                      fontSize: 12,
                      color: tokens.color.textMuted,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm }}>
                      <div style={{ width: 6, height: 6, borderRadius: tokens.radius.pill, backgroundColor: tokens.color.orange }} />
                      <span>Has gig</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm }}>
                      <div style={{ width: 12, height: 12, borderRadius: tokens.radius.sm, backgroundColor: tokens.color.green }} />
                      <span>Confirmed</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm }}>
                      <div style={{ width: 12, height: 12, borderRadius: tokens.radius.sm, backgroundColor: tokens.color.amber }} />
                      <span>Pending</span>
                    </div>
                  </div>
                </>
              )}

              {viewMode === 'week' && (
                <>
                  <WeekView />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space.lg,
                      marginTop: tokens.space.lg,
                      paddingTop: tokens.space.lg,
                      borderTop: tokens.border.hairline,
                      fontSize: 12,
                      color: tokens.color.textMuted,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm }}>
                      <div style={{ width: 12, height: 12, borderRadius: tokens.radius.sm, backgroundColor: tokens.color.orange }} />
                      <span>Gig</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm }}>
                      <div style={{ width: 12, height: 12, borderRadius: tokens.radius.sm, backgroundColor: "rgba(82, 82, 91, 0.5)" }} />
                      <span>Calendar event</span>
                    </div>
                  </div>
                </>
              )}

              {viewMode === 'day' && (
                <>
                  <DayView />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space.lg,
                      marginTop: tokens.space.lg,
                      paddingTop: tokens.space.lg,
                      borderTop: tokens.border.hairline,
                      fontSize: 12,
                      color: tokens.color.textMuted,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm }}>
                      <div style={{ width: 12, height: 12, borderRadius: tokens.radius.sm, backgroundColor: tokens.color.orange }} />
                      <span>Gig</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm }}>
                      <div style={{ width: 12, height: 12, borderRadius: tokens.radius.sm, backgroundColor: "rgba(82, 82, 91, 0.5)" }} />
                      <span>Calendar event</span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Day detail sidebar — month view only, hidden on mobile */}
          {viewMode === 'month' && (
            <div className="hidden md:block">
              <Card style={{ background: tokens.color.cardBg, border: tokens.border.card }}>
                <CardHeader style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: tokens.space.sm }}>
                  <CardTitle
                    style={{
                      fontFamily: tokens.font.display,
                      fontWeight: 600,
                      fontSize: 18,
                      letterSpacing: "-0.01em",
                      color: tokens.color.text,
                    }}
                  >
                    {format(selectedDate, 'EEEE, MMMM d')}
                  </CardTitle>
                  <AddGigMenu date={selectedDate} variant="full" testId="calendar-month-sidebar-add-gig" />
                </CardHeader>
                <CardContent>
                  {selectedDateGigs.length === 0 && selectedDateBgEvents.length === 0 ? (
                    <p style={{ margin: 0, ...tokens.type.bodySm, color: tokens.color.textMuted }}>
                      No gigs on this date
                    </p>
                  ) : (
                    <div className="overflow-y-auto max-h-[500px] space-y-3">
                      {selectedDateGigs.map(gig => (
                        <div
                          key={gig.gig_id}
                          onClick={() => navigate('/gigs', { state: { highlightGigId: gig.gig_id } })}
                          className="cursor-pointer hover:opacity-90 transition-opacity overflow-hidden"
                          style={{
                            borderRadius: tokens.radius.md,
                            borderLeft: `3px solid ${tokens.color.orange}`,
                            backgroundColor: 'rgba(245, 166, 35, 0.10)',
                          }}
                        >
                          <div style={{ padding: `${tokens.space.md}px ${tokens.space.md}px ${tokens.space.sm}px` }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                              <span style={{ ...tokens.type.bodySm, fontWeight: 500, color: tokens.color.orange }}>
                                {gig.artist_name}
                              </span>
                              <Badge className={`${getStatusBadgeColor(gig.status)} text-white text-xs`}>
                                {gig.status || 'confirmed'}
                              </Badge>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: tokens.space.sm,
                                ...tokens.type.bodySm,
                                color: tokens.color.textMuted,
                              }}
                            >
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {gig.venue_name}, {gig.city}
                              </span>
                            </div>
                            {gig.start_time && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: tokens.space.sm,
                                  marginTop: 4,
                                  ...tokens.type.bodySm,
                                  color: tokens.color.textMuted,
                                }}
                              >
                                <Clock className="h-3 w-3 shrink-0" />
                                <span>{gig.start_time}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {selectedDateBgEvents.length > 0 && (
                        <>
                          {selectedDateGigs.length > 0 && (
                            <div style={{ borderTop: tokens.border.hairline }} />
                          )}
                          {selectedDateBgEvents.map(evt => (
                            <div
                              key={evt.id}
                              className="overflow-hidden"
                              style={{
                                borderRadius: tokens.radius.md,
                                border: tokens.border.inner,
                                background: tokens.color.innerBg,
                              }}
                            >
                              <div style={{ padding: `${tokens.space.md}px ${tokens.space.md}px ${tokens.space.sm}px` }}>
                                <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm, marginBottom: 4 }}>
                                  <CalendarIcon className="h-3.5 w-3.5 shrink-0" style={{ color: tokens.color.textDim }} />
                                  <span
                                    style={{
                                      ...tokens.type.bodySm,
                                      fontWeight: 500,
                                      color: tokens.color.textDim,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {evt.title}
                                  </span>
                                </div>
                                {evt.calendar_name && (
                                  <p style={{ margin: 0, marginLeft: 20, fontSize: 12, color: tokens.color.textMuted }}>
                                    {evt.calendar_name}
                                  </p>
                                )}
                                {evt.start_time && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: tokens.space.sm,
                                      marginTop: 4,
                                      marginLeft: 20,
                                      fontSize: 12,
                                      color: tokens.color.textMuted,
                                    }}
                                  >
                                    <Clock className="h-3 w-3 shrink-0" />
                                    <span>{evt.start_time}{evt.end_time ? ` – ${evt.end_time}` : ''}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Export buttons (below the calendar) */}
        <div
          className="flex flex-wrap"
          style={{ gap: tokens.space.sm, marginTop: tokens.space.xl }}
        >
          <Button variant="outline" onClick={exportIcal} data-testid="export-ical">
            <FileDown className="h-4 w-4 mr-2" />
            Export iCal
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-csv-dropdown">
                {user?.subscription?.is_pro
                  ? <FileSpreadsheet className="h-4 w-4 mr-2" />
                  : <Lock className="h-4 w-4 mr-2" />
                }
                Export CSV
                <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs font-normal" style={{ color: tokens.color.textMuted }}>Export format</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => exportCsv('songkick')}
                disabled={!!exporting}
                data-testid="export-songkick"
                className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer"
              >
                <span className="font-medium">Songkick</span>
                <span className="text-xs" style={{ color: tokens.color.textMuted }}>Downloads a CSV ready to upload at songkick.com &rarr; Tourbox</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => exportCsv('bandsintown')}
                disabled={!!exporting}
                data-testid="export-bandsintown"
                className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer"
              >
                <span className="font-medium">Bandsintown</span>
                <span className="text-xs" style={{ color: tokens.color.textMuted }}>Downloads a CSV ready to upload at artists.bandsintown.com &rarr; Bulk Upload</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => exportCsv('generic')}
                disabled={!!exporting}
                data-testid="export-generic"
                className="flex flex-col items-start gap-0.5 py-2.5 cursor-pointer"
              >
                <span className="font-medium">Generic CSV</span>
                <span className="text-xs" style={{ color: tokens.color.textMuted }}>Full data &middot; all gigs</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Export loading banner */}
        {exporting && (
          <div
            data-testid="export-loading"
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.space.sm,
              marginTop: tokens.space.md,
              padding: `10px ${tokens.space.lg}px`,
              borderRadius: tokens.radius.md,
              border: `1px solid ${tokens.color.orangeLine}`,
              background: tokens.color.orangeSoft,
              ...tokens.type.bodySm,
              color: tokens.color.text,
            }}
          >
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: tokens.color.orange }} />
            <span>Preparing {exporting === 'songkick' ? 'Songkick' : exporting === 'bandsintown' ? 'Bandsintown' : 'Generic'} export…</span>
          </div>
        )}

        {/* Platform Upload Help */}
        <div className="space-y-2" style={{ marginTop: tokens.space.xl }}>
          <Collapsible>
            <CollapsibleTrigger asChild>
              <button
                className="flex items-center gap-2 group"
                data-testid="upload-help-toggle"
                style={{
                  ...tokens.type.bodySm,
                  color: tokens.color.textMuted,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  transition: `color ${tokens.motion.fast}`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = tokens.color.text)}
                onMouseLeave={(e) => (e.currentTarget.style.color = tokens.color.textMuted)}
              >
                <HelpCircle className="h-4 w-4" />
                <span>How to upload CSVs to Songkick &amp; Bandsintown</span>
                <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  data-testid="songkick-help"
                  style={{
                    background: tokens.color.cardBg,
                    border: tokens.border.card,
                    borderRadius: tokens.radius.md,
                    padding: tokens.space.lg,
                  }}
                  className="space-y-2"
                >
                  <h4
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space.sm,
                      margin: 0,
                      ...tokens.type.bodySm,
                      fontWeight: 500,
                      color: tokens.color.text,
                    }}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: tokens.radius.pill, background: tokens.color.orange }} />
                    Songkick
                  </h4>
                  <ol
                    className="space-y-1.5 list-decimal list-inside"
                    style={{ fontSize: 12, color: tokens.color.textMuted, margin: 0 }}
                  >
                    <li>Log in to <span style={{ color: tokens.color.text }}>tourbox.songkick.com</span></li>
                    <li>Go to <span style={{ color: tokens.color.text }}>Touring &rarr; Import dates</span></li>
                    <li>Choose <span style={{ color: tokens.color.text }}>"Upload CSV"</span> and select the file</li>
                    <li>Map columns if prompted, then confirm</li>
                  </ol>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: tokens.space.sm,
                      marginTop: tokens.space.sm,
                      padding: tokens.space.sm,
                      borderRadius: tokens.radius.sm,
                      background: tokens.color.orangeSoft,
                      border: `1px solid ${tokens.color.orangeLine}`,
                    }}
                  >
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: tokens.color.orange }} />
                    <p style={{ margin: 0, fontSize: 12, color: tokens.color.orange, opacity: 0.9 }}>
                      Only upcoming gigs with a venue are exported. Past and private gigs are excluded.
                    </p>
                  </div>
                </div>

                <div
                  data-testid="bandsintown-help"
                  style={{
                    background: tokens.color.cardBg,
                    border: tokens.border.card,
                    borderRadius: tokens.radius.md,
                    padding: tokens.space.lg,
                  }}
                  className="space-y-2"
                >
                  <h4
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space.sm,
                      margin: 0,
                      ...tokens.type.bodySm,
                      fontWeight: 500,
                      color: tokens.color.text,
                    }}
                  >
                    {/* Bandsintown brand teal — third-party brand colour, stays inline */}
                    <div style={{ width: 8, height: 8, borderRadius: tokens.radius.pill, background: "#00CEC8" }} />
                    Bandsintown
                  </h4>
                  <ol
                    className="space-y-1.5 list-decimal list-inside"
                    style={{ fontSize: 12, color: tokens.color.textMuted, margin: 0 }}
                  >
                    <li>Log in to <span style={{ color: tokens.color.text }}>artists.bandsintown.com</span></li>
                    <li>Go to <span style={{ color: tokens.color.text }}>Events &rarr; Bulk Upload</span></li>
                    <li>Upload each CSV file (max 25 events per file)</li>
                    <li>If you have more than 25 gigs, a zip is downloaded — unzip and upload each file separately</li>
                  </ol>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: tokens.space.sm,
                      marginTop: tokens.space.sm,
                      padding: tokens.space.sm,
                      borderRadius: tokens.radius.sm,
                      background: "rgba(0, 206, 200, 0.10)",
                      border: "1px solid rgba(0, 206, 200, 0.20)",
                    }}
                  >
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#00CEC8" }} />
                    <p style={{ margin: 0, fontSize: 12, color: "rgba(0, 206, 200, 0.9)" }}>
                      Bandsintown limits uploads to 25 rows per file. If you have more, we split them into a zip automatically.
                    </p>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>

      {/* Mobile day-detail bottom sheet — month view only */}
      {viewMode === 'month' && mobileSheetOpen && (
        <div className="md:hidden fixed inset-0 z-40" onClick={closeSheet}>
          <div className="absolute inset-0 bg-black/60" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-card rounded-t-2xl border-t border-border flex flex-col"
            style={{
              height: '85vh',
              paddingBottom: 'env(safe-area-inset-bottom, 16px)',
              transform: `translateY(${sheetDragY}px)`,
              // No transition while the finger is down → 1:1 tracking.
              // On release we re-enable it so snap-back / dismiss eases out.
              transition: sheetDragging ? 'none' : 'transform 0.25s cubic-bezier(0.32, 0.72, 0, 1)',
              // Constant (never toggled) so the entry slide runs exactly once
              // on mount and can't re-fire mid-gesture — that re-fire was the
              // tiny "bounce" on horizontal swipes.
              animation: 'slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
            }}
            onClick={e => e.stopPropagation()}
            onTouchStart={onSheetSwipeStart}
            onTouchMove={onSheetSwipeMove}
            onTouchEnd={onSheetSwipeEnd}
            onTouchCancel={onSheetSwipeEnd}
          >
            <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
            {/* Drag handle pill */}
            <div className="flex justify-center pt-2.5 pb-1 shrink-0">
              <div className="w-9 h-1 rounded-full bg-border" />
            </div>
            <div className="px-5 pt-2 pb-3 border-b border-border shrink-0">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setSelectedDate(subDays(selectedDate, 1))}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 -ml-1"
                  aria-label="Previous day"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <h3 className="font-heading font-semibold text-base">
                  {format(selectedDate, 'EEEE, MMMM d')}
                </h3>
                <button
                  onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 -mr-1"
                  aria-label="Next day"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="flex justify-center mt-2">
                <AddGigMenu
                  date={selectedDate}
                  variant="compact"
                  testId="calendar-month-sheet-add-gig"
                  onBeforeNavigate={() => setMobileSheetOpen(false)}
                />
              </div>
            </div>

            <div ref={sheetContentRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
              {selectedDateGigs.length === 0 && selectedDateBgEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No gigs on this date</p>
              ) : (
                [
                  ...selectedDateGigs.map(g => ({ kind: 'gig', data: g, t: g.start_time || '99:99' })),
                  ...selectedDateBgEvents.map(e => ({ kind: 'bg', data: e, t: e.start_time || '99:99' })),
                ]
                  .sort((a, b) => a.t.localeCompare(b.t))
                  .map(item =>
                    item.kind === 'gig' ? (
                      <div
                        key={`gig-${item.data.gig_id}`}
                        className="rounded-lg cursor-pointer hover:opacity-90 transition-opacity overflow-hidden"
                        style={{ borderLeft: `3px solid ${tokens.color.orange}`, backgroundColor: tokens.color.orangeSoft }}
                        onClick={() => { setMobileSheetOpen(false); navigate('/gigs', { state: { highlightGigId: item.data.gig_id } }); }}
                      >
                        <div className="px-3 pt-3 pb-2">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-medium text-sm" style={{ color: tokens.color.orange }}>
                              {item.data.artist_name}
                            </span>
                            <Badge className={`${getStatusBadgeColor(item.data.status)} text-white text-xs`}>
                              {item.data.status || 'confirmed'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{item.data.venue_name}, {item.data.city}</span>
                          </div>
                          {item.data.start_time && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                              <Clock className="h-3 w-3 shrink-0" />
                              <span>{item.data.start_time}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        key={`bg-${item.data.id}`}
                        className="rounded-lg border border-border bg-muted/50 overflow-hidden"
                      >
                        <div className="px-3 pt-3 pb-2">
                          <div className="flex items-center gap-2 mb-1">
                            <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <span className="text-sm font-medium text-foreground truncate">{item.data.title}</span>
                          </div>
                          {item.data.calendar_name && (
                            <p className="text-xs text-muted-foreground ml-5">{item.data.calendar_name}</p>
                          )}
                          {item.data.start_time && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 ml-5">
                              <Clock className="h-3 w-3 shrink-0" />
                              <span>{item.data.start_time}{item.data.end_time ? ` – ${item.data.end_time}` : ''}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  )
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CalendarView;
